import type React from "react"
import { useTranslation } from "react-i18next"
import { useState, useEffect, useRef } from "react"
import {
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material"
import { 
  Delete as DeleteIcon, 
  Close as CloseIcon, 
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CameraAlt as CameraAltIcon,
  PhotoLibrary as PhotoLibraryIcon,
  DeleteForever as DeleteForeverIcon,
} from "@mui/icons-material"
import EntityList from "../../components/EntityList"
import api from "../../services/api"
import type { Horse, Tenant } from "@/src/interfaces"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation, useAppNavigation } from "../../App"
import { HorseImage } from "../../components/HorseImage"
import { HorseImageService } from "../../services/horseImageService"

// Interface para a resposta da API que pode conter um array de cavalos
interface ApiResponse {
  horses?: Horse[]
  [key: string]: any
}

export default function HorseList() {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation()
  const { getParam } = useAppNavigation()
  
  // Obtém tenant_id do parâmetro criptografado se disponível, senão usa 1 como padrão
  const tenantIdFromParams = getParam('tenantId')
  const tenant_id = tenantIdFromParams ? Number(tenantIdFromParams) : 1
  
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()

  // Estados principais
  const [selectedHorse, setSelectedHorse] = useState<Horse | null>(null)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)
  // Filtro de prenhez: all | pregnant | notPregnant
  const [pregnancyFilter, setPregnancyFilter] =
    useState<"all" | "pregnant" | "notPregnant">("all")
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loadingTenant, setLoadingTenant] = useState<boolean>(true)
  
  // Estados para gerenciamento de foto
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false)
  const [photoMessage, setPhotoMessage] = useState<string | null>(null)
  const [photoMessageType, setPhotoMessageType] = useState<'success' | 'error'>('success')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Estados para o menu de edição de foto
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState<HTMLElement | null>(null)
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false)
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)

  console.log("HorseList - Componente renderizado com tenant_id:", tenant_id)

  // Função para converter arquivo para base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = error => reject(error)
    })
  }

  // Função para validar arquivo de imagem
  const validateImageFile = (file: File): string | null => {
    const maxSize = 5 * 1024 * 1024 // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

    if (!allowedTypes.includes(file.type)) {
      return t("horses.unsupportedFileType", { types: "JPEG, PNG, GIF ou WebP" })
    }

    if (file.size > maxSize) {
      return t("horses.fileTooLarge", { maxSize: "5MB" })
    }

    return null
  }

  // Função para lidar com upload de foto
  const handlePhotoUpload = async (file: File, horseId: number) => {
    const validationError = validateImageFile(file)
    if (validationError) {
      setPhotoMessage(validationError)
      setPhotoMessageType('error')
      return
    }

    setUploadingPhoto(true)
    setPhotoMessage(null)

    try {
      const base64Image = await fileToBase64(file)
      
      const response = await api.put(`/horse/${tenant_id}/${horseId}`, {
        horsePhoto: base64Image
      })
      
      console.log("✅ Foto do cavalo atualizada:", response.data)
      
      // Limpar cache da imagem para forçar recarregamento
      HorseImageService.removeCacheEntry(tenant_id, horseId)
      
      setPhotoMessage("Foto atualizada com sucesso!")
      setPhotoMessageType('success')
      
      // Força atualização imediata da imagem
      setRefreshTrigger(prev => prev + 1)
      
    } catch (error) {
      console.error("❌ Erro ao atualizar foto:", error)
      setPhotoMessage("Erro ao atualizar foto. Tente novamente.")
      setPhotoMessageType('error')
    } finally {
      setUploadingPhoto(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  // Função para "apagar" foto do cavalo (volta para imagem padrão localmente)
  const handleDeletePhoto = async (horseId: number) => {
    if (!window.confirm(t("horses.confirmDeleteImage"))) {
      return
    }

    setUploadingPhoto(true)
    setPhotoMessage(null)

    try {
      // Simular um pequeno delay para dar feedback visual
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Limpar cache da imagem para forçar recarregamento com imagem padrão
      HorseImageService.removeCacheEntry(tenant_id, horseId)
      
      // Forçar atualização imediata da imagem
      setRefreshTrigger(prev => prev + 1)
      
      setPhotoMessage(t("horses.defaultImageApplied"))
      setPhotoMessageType('success')
      
      console.log("✅ Foto removida localmente, voltando para imagem padrão")
      
    } catch (error: any) {
      console.error("❌ Erro ao aplicar imagem padrão:", error)
      setPhotoMessage(t("horses.applyDefaultImageError"))
      setPhotoMessageType('error')
    } finally {
      setUploadingPhoto(false)
      setPhotoMenuAnchor(null)
    }
  }

  // Função para iniciar a câmera
  const startCamera = async () => {
    setCameraError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 }, 
          height: { ideal: 720 },
          facingMode: 'user' // ou 'environment' para câmera traseira
        } 
      })
      setCameraStream(stream)
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play()
      }
    } catch (error) {
      console.error("Erro ao acessar a câmera:", error)
      setCameraError(t("horses.cameraAccessError"))
    }
  }

  // Função para parar a câmera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => {
        track.stop()
      })
      setCameraStream(null)
    }
  }

  // Função para capturar foto da câmera
  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current || !selectedHorse) return

    const canvas = canvasRef.current
    const video = videoRef.current
    
    // Ajustar o canvas para o mesmo tamanho do vídeo
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    const context = canvas.getContext('2d')
    if (!context) return

    // Desenhar o frame atual do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    
    // Converter canvas para blob
    canvas.toBlob(async (blob) => {
      if (!blob) return
      
      // Converter blob para File
      const file = new File([blob], `foto-cavalo-${selectedHorse.horseId}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      })
      
      // Fechar diálogo da câmera
      setCameraDialogOpen(false)
      stopCamera()
      setPhotoMenuAnchor(null)
      
      // Upload da foto
      await handlePhotoUpload(file, selectedHorse.horseId)
    }, 'image/jpeg', 0.9) // Qualidade da imagem
  }

  // Função para abrir menu de edição de foto
  const handleEditPhoto = (horseId: number, event: React.MouseEvent<HTMLElement>) => {
    if (uploadingPhoto) return
    setPhotoMenuAnchor(event.currentTarget)
  }

  // Função para fechar menu de edição de foto
  const handleClosePhotoMenu = () => {
    setPhotoMenuAnchor(null)
  }

  // Função para escolher da biblioteca
  const handleChooseFromLibrary = () => {
    setPhotoMenuAnchor(null)
    fileInputRef.current?.click()
  }

  // Função para abrir câmera
  const handleOpenCamera = () => {
    setPhotoMenuAnchor(null)
    setCameraDialogOpen(true)
    startCamera()
  }

  // Função para fechar diálogo da câmera
  const handleCloseCameraDialog = () => {
    setCameraDialogOpen(false)
    stopCamera()
  }

  // Limpar stream da câmera quando componente for desmontado
  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [])

  const handleBackHome = () => {
    navigateToPage('dashboard')
  }

  // Função para lidar com seleção de arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !selectedHorse) return
    
    handlePhotoUpload(file, selectedHorse.horseId)
  }

  // Função para navegar para novo cavalo
  const handleNew = () => {
    console.log("🚀 Navegando para novo cavalo");
    navigateToPage('cavalos', { mode: 'new', tenantId: String(tenant_id) });
  };

  // Função para navegar para edição - CORRIGIDA
  const handleEdit = (horseId: number | string, e?: React.MouseEvent) => {
    console.log("✏️ Navegando para editar cavalo ID:", horseId);
    if (e) {
      e.stopPropagation();
    }
    navigateToPage('cavalos', { mode: 'edit', id: String(horseId), tenantId: String(tenant_id) });
  };

  // Buscar informações do tenant
  useEffect(() => {
    const fetchTenant = async () => {
      try {
        setLoadingTenant(true)
        console.log("Buscando tenant com ID:", tenant_id)
        const response = await api.get(`/tenant/${tenant_id}`)
        
        // Processar a resposta para extrair o tenant
        let tenantData = null

        if (response.data) {
          // Caso 1: response.data.tenant
          if (response.data.tenant) {
            tenantData = response.data.tenant
          } 
          // Caso 2: response.data é o próprio tenant
          else if (response.data.tenantId && response.data.tenantName) {
            tenantData = response.data
          }
          // Caso 3: response.data tem a estrutura do array de tenants
          else if (response.data.tenants && Array.isArray(response.data.tenants)) {
            const foundTenant = response.data.tenants.find((t: any) => t.tenantId === tenant_id)
            if (foundTenant) {
              tenantData = foundTenant
            }
          }
          // Caso 4: response.data é um array direto
          else if (Array.isArray(response.data)) {
            const foundTenant = response.data.find((t: any) => t.tenantId === tenant_id)
            if (foundTenant) {
              tenantData = foundTenant
            }
          }
        }

        if (tenantData) {
          console.log("✅ Tenant carregado com sucesso:", tenantData)
          setTenant(tenantData)
        } else {
          console.error("❌ Não foi possível extrair os dados do tenant da resposta")
        }
      } catch (error) {
        console.error("Erro ao carregar informações do tenant:", error)
      } finally {
        setLoadingTenant(false)
      }
    }

    fetchTenant()
  }, [tenant_id])

  // Função para navegar de volta para a lista de tenants
  const handleBackToTenants = () => {
    navigateToPage('proprietarios', { mode: 'list' })
  }

  // Funções de formatação
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    
    // Se a data vem no formato ISO (YYYY-MM-DD ou com timestamp)
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('T')[0].split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Assumindo que a data vem no formato DD/MM/YYYY
    const parts = dateString.split('/');
    if (parts.length !== 3) return t("horses.invalidDate");

    const [day, month, year] = parts;

    // Validação básica
    if (parseInt(day) < 1 || parseInt(day) > 31 ||
        parseInt(month) < 1 || parseInt(month) > 12) {
      return t("horses.invalidDate");
    }
    
    return `${day}/${month}/${year}`;
  };

  const formatGender = (gender: string) => {
    switch (gender) {
      case "M": return t("common.male")
      case "F": return t("common.female")
      default: return gender
    }
  }

  // Função corrigida para formatar booleanos
  const formatBoolean = (value: any) => {
    // Tratar 1, "1", true como Sim
    if (value === 1 || value === "1" || value === true) return "Sim"
    // Tratar 0, "0", false, null, undefined como Não
    return "Não"
  }

  // Função para mostrar detalhes
  const handleShowDetails = (horse: Horse, e?: React.MouseEvent) => {
    console.log("HorseList - handleShowDetails chamado com:", horse)

    if (e) {
      e.stopPropagation()
    }
    
    setSelectedHorse(horse)
    setDetailsModalOpen(true)
  }

  // Função para excluir um cavalo
  const handleDelete = async (horseId: number, e?: React.MouseEvent) => {
    console.log(`HorseList - handleDelete chamado com ID: ${horseId}`)

    if (e) {
      e.stopPropagation()
    }

    if (!window.confirm(t("horses.confirmDelete"))) {
      return
    }

    try {
      await api.delete(`/horse/${tenant_id}/${horseId}`)
      console.log("HorseList - Cavalo excluído com sucesso")

      if (detailsModalOpen) {
        setDetailsModalOpen(false)
      }

      setRefreshTrigger((prev) => prev + 1)
    } catch (err) {
      console.error("HorseList - Erro ao excluir cavalo:", err)
      alert("Ocorreu um erro ao excluir o cavalo. Por favor, tente novamente.")
    }
  }

  // Configuração das colunas para o EntityList
  const columns = [
    {
      id: "horseName",
      label: t("fields.name"),
      minWidth: 170,
    },
    {
      id: "horseGender",
      label: t("fields.gender"),
      minWidth: 100,
      align: "center" as const,
      mobileHideLabel: true,
      format: (value: string, row: Horse) => (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Chip
            label={formatGender(value)}
            color={value === "F" ? "secondary" : "primary"}
            size="small"
            variant="outlined"
          />
          {value === "F" && Number(row.horsePregnant) === 1 && (
            <Chip
              label={t("horses.pregnant")}
              color="warning"
              size="small"
              variant="filled"
            />
          )}
        </Box>
      ),
    },
    {
      id: "horseBirth",
      label: t("fields.birthDate"),
      minWidth: 150,
      format: (value: string | null) => formatDate(value),
      hide: true,
    },
    {
      // Data prevista de parto — só relevante em prenhas; visível em telas largas.
      id: "horseChildBirth",
      label: t("horses.expectedBirth"),
      minWidth: 140,
      format: (value: string | null, row: Horse) =>
        Number(row.horsePregnant) === 1 && value ? formatDate(value) : "-",
      hide: isMobile || isTablet,
    },
    {
      id: "horseBreedName",
      label: t("fields.breed"),
      minWidth: 170,
      // visível no mobile (1ª info textual útil além do nome)
    },
    {
      id: "stableName",
      label: t("fields.stable"),
      minWidth: 170,
      hide: isMobile || isTablet,
    },
    {
      id: "stallId",
      label: t("stalls.nameLabel"),
      minWidth: 120,
      format: (value: number | null, row: Horse) => {
        if (!value) return "-"
        return row.stallName || `Baia ${value}`
      },
      hide: isMobile || isTablet,
    },
  ]
  
  // Transformação de dados
  const transformData = (data: any[] | ApiResponse): Horse[] => {
    console.log("HorseList - transformData recebeu:", data)

    let list: Horse[] = []
    if (Array.isArray(data)) {
      list = data as Horse[]
    } else if (
      data &&
      typeof data === "object" &&
      "horses" in data &&
      Array.isArray(data.horses)
    ) {
      list = data.horses
    } else {
      console.warn("HorseList - Formato de dados não reconhecido")
      return []
    }

    // Filtro de prenhez (só fêmeas podem estar prenhas).
    if (pregnancyFilter === "pregnant") {
      list = list.filter((h) => Number(h.horsePregnant) === 1)
    } else if (pregnancyFilter === "notPregnant") {
      list = list.filter((h) => Number(h.horsePregnant) !== 1)
    }
    return list
  }

  return (
    <>
      {/* Breadcrumbs */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: isMobile ? "100%" : "auto" }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ flexWrap: "wrap" }}>
              {loadingTenant ? (
                <CircularProgress size={20} />
              ) : (
                <Typography
                  color="text.primary"
                  sx={{
                    maxWidth: { xs: "150px", sm: "300px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {(() => {
                    const possibleNames = [
                      tenant?.tenantName,
                      tenant?.name,
                      tenant?.nome,
                      tenant?.razaoSocial,
                      tenant?.nomeFantasia
                    ];
                    
                    return possibleNames.find(name => name && typeof name === 'string') || `Tenant ${tenant_id}`;
                  })()}
                </Typography>
              )}
            </Breadcrumbs>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="pregnancy-filter-label">
                {t("horses.pregnancyFilter")}
              </InputLabel>
              <Select
                labelId="pregnancy-filter-label"
                label={t("horses.pregnancyFilter")}
                value={pregnancyFilter}
                onChange={(e) => {
                  setPregnancyFilter(
                    e.target.value as "all" | "pregnant" | "notPregnant",
                  )
                  setRefreshTrigger((prev) => prev + 1)
                }}
              >
                <MenuItem value="all">{t("horses.filterAll")}</MenuItem>
                <MenuItem value="pregnant">{t("horses.pregnant")}</MenuItem>
                <MenuItem value="notPregnant">
                  {t("horses.notPregnant")}
                </MenuItem>
              </Select>
            </FormControl>
            <Button
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              onClick={handleBackHome}
              size={isMobile ? "small" : "medium"}
              sx={{ mt: isMobile ? 1 : 0 }}
            >
              {t("common.back")}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Lista de Cavalos */}
      <EntityList
        title={t("nav.horses")}
        endpoint={`/horse/${tenant_id}`}
        columns={columns}
        addPath=""
        editPathPrefix=""
        idField="horseId"
        refreshTrigger={refreshTrigger}
        transformData={transformData}
        emptyMessage={t("horses.emptyMessage")}
        addButtonLabel={t("horses.newTitle")}
        deleteConfirmationMessage="Tem certeza que deseja excluir este cavalo?"
        onRowClick={(row) => handleShowDetails(row as Horse)}
        useEncryptedNavigation={true}
        encryptedPageName="cavalos"
        onAddClick={handleNew}
        onEditClick={handleEdit}
        extraParams={{ tenantId: String(tenant_id) }}
        entityType = 'horses'
      />

      {/* Modal de Detalhes */}
      <Dialog
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
      >
        {selectedHorse && (
          <>
            <DialogTitle>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                  Detalhes do Cavalo: {selectedHorse.horseName}
                  <Chip
                    label={formatGender(selectedHorse.horseGender)}
                    color={selectedHorse.horseGender === "F" ? "secondary" : "primary"}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 2 }}
                  />
                </Typography>
                <IconButton onClick={() => setDetailsModalOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Grid principal com melhor responsividade */}
              <Grid container spacing={3} sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Container da Foto - Sempre centralizado */}
                <Grid item xs={12} lg={4}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: { xs: 'auto', lg: '400px' }, // Altura mínima em desktop
                    textAlign: 'center'
                  }}>
                    {/* Container da imagem com botão */}
                    <Box sx={{ 
                      position: 'relative', 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      {/* Overlay de loading durante upload */}
                      {uploadingPhoto && (
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 10,
                          width: isMobile ? 200 : 300,
                          height: isMobile ? 200 : 300,
                          margin: '0 auto'
                        }}>
                          <Box sx={{ textAlign: 'center', color: 'white' }}>
                            <CircularProgress color="inherit" size={40} />
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              Processando...
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      
                      {/* Componente da imagem */}
                      <HorseImage 
                        tenantId={tenant_id}
                        horseId={selectedHorse.horseId}
                        horseName={selectedHorse.horseName}
                        height={isMobile ? 200 : 300}
                        circular={true}
                        showRetryButton={false}
                        key={`horse-image-${selectedHorse.horseId}-${refreshTrigger}`}
                      />
                      
                      {/* Botão de editar foto - sempre centralizado com a foto */}
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={uploadingPhoto ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
                        onClick={(e) => handleEditPhoto(selectedHorse.horseId, e)}
                        disabled={uploadingPhoto}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          minWidth: { xs: '140px', sm: '160px' } // Largura mínima para consistência
                        }}
                      >
                        {uploadingPhoto ? 'Processando...' : 'Editar Foto'}
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                {/* Informações do Cavalo */}
                <Grid item xs={12} lg={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                        Informações Básicas
                      </Typography>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>ID:</strong> {selectedHorse.horseId}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>Nome:</strong> {selectedHorse.horseName}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>Data de Nascimento:</strong> {formatDate(selectedHorse.horseBirth)}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>Raça:</strong> {selectedHorse.horseBreedName}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>Gênero:</strong> {formatGender(selectedHorse.horseGender)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                        Localização
                      </Typography>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>Estábulo:</strong> {selectedHorse.stableName || "-"}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>Baia:</strong> {selectedHorse.stallName || (selectedHorse.stallId ? `Baia ${selectedHorse.stallId}` : "-")}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                        Genealogia
                      </Typography>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>Pai:</strong> {selectedHorse.horseFather || "-"}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                          <strong>Mãe:</strong> {selectedHorse.horseMother || "-"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      {/* Características específicas por gênero */}
                      {selectedHorse.horseGender === "M" && (
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                            Características Específicas
                          </Typography>
                          <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                            <strong>Castrado:</strong> {formatBoolean(selectedHorse.horseCastrated)}
                          </Typography>
                        </Box>
                      )}

                      {selectedHorse.horseGender === "F" && (
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                            Características Reprodutivas
                          </Typography>
                          <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                            <strong>Prenha:</strong> {formatBoolean(selectedHorse.horsePregnant)}
                          </Typography>
                          {/* Mostrar informações de gravidez se prenha for verdadeiro */}
                          {(selectedHorse.horsePregnant === 1 ) && (
                            <>
                              <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                                <strong>Data Prevista do Parto:</strong> {formatDate(selectedHorse.horseChildBirth)}
                              </Typography>
                              <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                                <strong>Número de Filhotes:</strong> {selectedHorse.horseChildNumber || "-"}
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Menu de opções de foto */}
      <Menu
        anchorEl={photoMenuAnchor}
        open={Boolean(photoMenuAnchor)}
        onClose={handleClosePhotoMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleOpenCamera}>
          <ListItemIcon>
            <CameraAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("horses.takePhoto")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChooseFromLibrary}>
          <ListItemIcon>
            <PhotoLibraryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("horses.choosePhoto")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedHorse && handleDeletePhoto(selectedHorse.horseId)}>
          <ListItemIcon>
            <DeleteForeverIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("horses.deletePhoto")}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog da Câmera */}
      <Dialog
        open={cameraDialogOpen}
        onClose={handleCloseCameraDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Tirar Foto</Typography>
            <IconButton onClick={handleCloseCameraDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {cameraError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cameraError}
            </Alert>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: 'auto',
                  borderRadius: '8px',
                  backgroundColor: '#000'
                }}
              />
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCameraDialog}>
            Cancelar
          </Button>
          {!cameraError && (
            <Button
              variant="contained"
              startIcon={<PhotoCameraIcon />}
              onClick={capturePhoto}
              disabled={!cameraStream}
            >
              Capturar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Input de arquivo hidden para upload de foto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Snackbar para feedback de ações da foto */}
      <Snackbar
        open={Boolean(photoMessage)}
        autoHideDuration={4000}
        onClose={() => setPhotoMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setPhotoMessage(null)} 
          severity={photoMessageType}
          sx={{ width: '100%' }}
        >
          {photoMessage}
        </Alert>
      </Snackbar>
    </>
  )
}